/**
 * LR Direct — Dynamic per-stop tracker derivation.
 *
 * The tracker's stage list is built from the dispatch's actual stops so
 * multi-pickup / multi-drop loads render a circle per checkpoint.
 *
 * Ordered stages produced:
 *   Posted → Awarded → Dispatch Created
 *     → Truck En Route
 *     → (per pickup)  Pickup [N] Checked In → Pickup [N] Loaded
 *     → In Transit
 *     → (per drop)    Delivery [N] Checked In → Delivery [N] Delivered
 *     → Delivered (auto)
 *     → POD Uploaded (auto)
 *     → Closed (auto)
 *
 * Legacy status_* events (status_assigned, status_picked_up,
 * status_en_route_to_delivery, status_delivered) are still consulted as
 * fallbacks so existing dispatches without per-stop status keep their
 * circles filled.
 */

export type StageStatus = "done" | "current" | "future" | "skipped";
export type StageKind = "system" | "broker_dispatch" | "broker_stop" | "auto";
export type StopSubKind = "checked_in" | "done";
export type StageKey = string;

export interface DerivedStage {
  key: StageKey;
  label: string;
  /** Short, one-word-ish label rendered UNDER the circle. */
  shortLabel: string;
  /** Small ALL-CAPS header rendered ABOVE the circle (e.g. "PICKUP 1"). Empty string for non-stop stages. */
  groupHeader: string;
  status: StageStatus;
  at: string | null;
  kind: StageKind;
  stopId?: string;
  stopType?: "pickup" | "delivery";
  stopNumber?: number;
  subKind?: StopSubKind;
}

export interface StopWithStatus {
  id?: string;
  stop_number: number;
  stop_type: "pickup" | "delivery";
  status?: string | null;
  checked_in_at?: string | null;
  completed_at?: string | null;
}

export interface DeriveInput {
  load: {
    status?: string | null;
    posted_at?: string | null;
    awarded_at?: string | null;
  } | null;
  dispatch: {
    id?: string | null;
    created_at?: string | null;
    status?: string | null;
    pickup_eta_at?: string | null;
    delivery_eta_at?: string | null;
  } | null;
  stops?: StopWithStatus[];
  events?: Array<{ event_type: string; created_at: string; payload?: any }>;
  podDoc?: { created_at: string } | null;
  /** Back-compat only — ignored. Older callers passed this. */
  firstPickupStop?: unknown;
}

function firstEventAt(
  events: Array<{ event_type: string; created_at: string; payload?: any }> | undefined,
  type: string,
  predicate?: (e: { event_type: string; created_at: string; payload?: any }) => boolean,
): string | null {
  if (!events?.length) return null;
  const sorted = [...events].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  return sorted.find((e) => e.event_type === type && (!predicate || predicate(e)))?.created_at ?? null;
}

export function stopEventType(
  stopType: "pickup" | "delivery",
  n: number,
  sub: StopSubKind,
): string {
  if (sub === "checked_in") return `${stopType}_${n}_checked_in`;
  return stopType === "pickup" ? `pickup_${n}_loaded` : `delivery_${n}_delivered`;
}

function stopLabel(
  stopType: "pickup" | "delivery",
  n: number,
  totalOfType: number,
  sub: StopSubKind,
): string {
  const noun = stopType === "pickup" ? "Pickup" : "Delivery";
  const prefix = totalOfType > 1 ? `${noun} ${n}` : noun;
  if (sub === "checked_in") return `${prefix} Checked In`;
  return stopType === "pickup" ? `${prefix} Loaded` : `${prefix} Delivered`;
}

export function deriveStages(input: DeriveInput): DerivedStage[] {
  const { load, dispatch, stops, events, podDoc } = input;

  const pickups = (stops ?? [])
    .filter((s) => s.stop_type === "pickup")
    .sort((a, b) => a.stop_number - b.stop_number);
  const deliveries = (stops ?? [])
    .filter((s) => s.stop_type === "delivery")
    .sort((a, b) => a.stop_number - b.stop_number);

  // Fallback: no stops loaded yet — synth 1 pickup + 1 delivery so tracker still renders.
  const pickupList: StopWithStatus[] =
    pickups.length > 0 ? pickups : [{ stop_number: 1, stop_type: "pickup" }];
  const deliveryList: StopWithStatus[] =
    deliveries.length > 0 ? deliveries : [{ stop_number: 1, stop_type: "delivery" }];

  type Draft = Omit<DerivedStage, "status" | "shortLabel" | "groupHeader">;
  const drafts: Draft[] = [];

  drafts.push({ key: "posted", label: "Posted", at: load?.posted_at ?? null, kind: "system" });
  drafts.push({ key: "awarded", label: "Awarded", at: load?.awarded_at ?? null, kind: "system" });
  drafts.push({
    key: "dispatch_created",
    label: "Dispatch Created",
    at: dispatch?.created_at ?? null,
    kind: "system",
  });

  // CANONICAL: only an explicit broker/carrier action fills Truck En Route.
  // `status_assigned` written automatically on dispatch acceptance
  // (payload.auto === true) is deliberately IGNORED here — accepting a
  // dispatch must never imply the truck is rolling. Legacy dispatches whose
  // `status_assigned` was set manually still resolve for back-compat.
  const truckEnRouteAt =
    firstEventAt(events, "status_truck_en_route") ??
    firstEventAt(events, "status_en_route_to_pickup") ??
    firstEventAt(events, "status_assigned", (e) => e.payload?.auto !== true);
  drafts.push({
    key: "truck_en_route",
    label: "Truck En Route",
    at: truckEnRouteAt,
    kind: "broker_dispatch",
  });

  // Per-pickup stages
  for (const p of pickupList) {
    const totalPickups = pickupList.length;
    const evCheckedIn = firstEventAt(events, stopEventType("pickup", p.stop_number, "checked_in"));
    const evDone = firstEventAt(events, stopEventType("pickup", p.stop_number, "done"));
    // Legacy fallback: `status_picked_up` fills stop 1 loaded on old dispatches.
    const legacyPickedUp =
      p.stop_number === 1 ? firstEventAt(events, "status_picked_up") : null;
    // Legacy/dropdown "At pickup" maps 1:1 onto Pickup 1 Checked In.
    const legacyAtPickup =
      p.stop_number === 1 ? firstEventAt(events, "status_at_pickup") : null;

    drafts.push({
      key: stopEventType("pickup", p.stop_number, "checked_in"),
      label: stopLabel("pickup", p.stop_number, totalPickups, "checked_in"),
      at:
        p.checked_in_at ??
        evCheckedIn ??
        legacyAtPickup ??
        (p.status === "done" ? evDone ?? legacyPickedUp : null),
      kind: "broker_stop",
      stopId: p.id,
      stopType: "pickup",
      stopNumber: p.stop_number,
      subKind: "checked_in",
    });
    drafts.push({
      key: stopEventType("pickup", p.stop_number, "done"),
      label: stopLabel("pickup", p.stop_number, totalPickups, "done"),
      at:
        (p.status === "done" ? p.completed_at : null) ??
        evDone ??
        legacyPickedUp,
      kind: "broker_stop",
      stopId: p.id,
      stopType: "pickup",
      stopNumber: p.stop_number,
      subKind: "done",
    });
  }

  const inTransitAt =
    firstEventAt(events, "status_in_transit") ??
    firstEventAt(events, "status_en_route_to_delivery");
  drafts.push({
    key: "in_transit",
    label: "In Transit",
    at: inTransitAt,
    kind: "broker_dispatch",
  });

  for (const d of deliveryList) {
    const totalDeliveries = deliveryList.length;
    const evCheckedIn = firstEventAt(events, stopEventType("delivery", d.stop_number, "checked_in"));
    const evDone = firstEventAt(events, stopEventType("delivery", d.stop_number, "done"));
    const legacyDelivered =
      d.stop_number === deliveryList.length
        ? firstEventAt(events, "status_delivered") ?? firstEventAt(events, "status_at_delivery")
        : null;

    drafts.push({
      key: stopEventType("delivery", d.stop_number, "checked_in"),
      label: stopLabel("delivery", d.stop_number, totalDeliveries, "checked_in"),
      at: d.checked_in_at ?? evCheckedIn ?? (d.status === "done" ? evDone ?? legacyDelivered : null),
      kind: "broker_stop",
      stopId: d.id,
      stopType: "delivery",
      stopNumber: d.stop_number,
      subKind: "checked_in",
    });
    // NOTE: per-stop "Delivery N Delivered" stage intentionally removed —
    // the collective "Delivered" auto stage below covers final delivery.
  }

  // Delivered (auto) — all deliveries done OR explicit legacy status_delivered.
  const allDeliveriesDone =
    deliveries.length > 0 && deliveries.every((d) => d.status === "done");
  const lastDeliveryAt =
    deliveries
      .map((d) => d.completed_at ?? null)
      .filter((x): x is string => !!x)
      .sort()
      .at(-1) ?? null;
  // CANONICAL: arriving at delivery ("status_at_delivery") fills only
  // Delivery Checked In — never Delivered.
  const deliveredExplicit = firstEventAt(events, "status_delivered");
  drafts.push({
    key: "delivered",
    label: "Delivered",
    at: allDeliveriesDone ? (lastDeliveryAt ?? deliveredExplicit) : deliveredExplicit,
    kind: "auto",
  });

  drafts.push({
    key: "pod_uploaded",
    label: "POD Uploaded",
    at: podDoc?.created_at ?? null,
    kind: "auto",
  });

  const closedAt =
    firstEventAt(events, "status_closed") ??
    (load?.status === "closed" || dispatch?.status === "closed" ? podDoc?.created_at ?? null : null);
  drafts.push({
    key: "closed",
    label: "Closed",
    at: closedAt,
    kind: "auto",
  });

  // Assign statuses + derive header/short label. Header/short is only set for
  // per-stop broker stages so the tracker can render "PICKUP 1" above and
  // "Checked In"/"Loaded" below the circle.
  function headerFor(d: Draft): string {
    if (d.kind !== "broker_stop" || !d.stopType || !d.stopNumber) return "";
    return `${d.stopType.toUpperCase()} ${d.stopNumber}`;
  }
  function shortFor(d: Draft): string {
    if (d.kind === "broker_stop" && d.subKind) {
      if (d.subKind === "checked_in") return "Checked In";
      return d.stopType === "pickup" ? "Loaded" : "Delivered";
    }
    return d.label;
  }

  let currentAssigned = false;
  return drafts.map((d): DerivedStage => {
    const base = { ...d, groupHeader: headerFor(d), shortLabel: shortFor(d) };
    if (d.at) return { ...base, status: "done" };
    if (!currentAssigned) {
      currentAssigned = true;
      return { ...base, status: "current" };
    }
    return { ...base, status: "future" };
  });
}
